import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Badge } from "@/components/common";
import { toast } from "sonner";
import { ExternalLink, Plus, FlaskConical } from "lucide-react";
import { NuanceThread } from "@/components/common";

export default function Science() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [studies, setStudies] = useState([]);
  const [areas, setAreas] = useState([]);
  const [fTopic, setFTopic] = useState("");
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ title: "", abstract: "", source_url: "", topics: "" });

  const load = () => api.get(`/studies${fTopic ? `?topic=${encodeURIComponent(fTopic)}` : ""}`).then((r) => setStudies(r.data));
  useEffect(() => { load(); }, [fTopic]);
  useEffect(() => { api.get("/policy-areas").then((r) => setAreas(r.data.areas)); }, []);

  const publish = async (e) => {
    e.preventDefault();
    try {
      await api.post("/studies", { ...form, topics: form.topics.split(",").map((x) => x.trim()).filter(Boolean) });
      toast.success(t("Study published")); setShow(false); setForm({ title: "", abstract: "", source_url: "", topics: "" }); load();
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail)); }
  };
  const addNuance = async (id, text, sources) => {
    try { await api.post(`/studies/${id}/nuance`, { text, sources }); toast.success(t("Nuance added")); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); throw e; }
  };

  return (
    <div className="space-y-6" data-testid="science-page">
      <div className="flex items-end justify-between border-b-2 border-foreground pb-3">
        <div><p className="overline mb-1">{t("Certified research · platform-wide by topic")}</p><h1 className="font-display text-3xl font-extrabold tracking-tight">{t("Science & Studies")}</h1></div>
        {user.user_type === "research_organisation" && (
          <button data-testid="study-new-btn" onClick={() => setShow(!show)} className="flex items-center gap-1.5 bg-primary text-white px-4 py-2 rounded-sm text-sm font-semibold hover:bg-primary/90 transition-colors"><Plus size={16} /> {t("Publish study")}</button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2" data-testid="science-filters">
        <button onClick={() => setFTopic("")} className={`text-xs px-3 py-1 rounded-sm border ${fTopic === "" ? "bg-primary text-white border-primary" : "border-border text-muted-foreground hover:border-primary"}`}>{t("All topics")}</button>
        {areas.map((a) => <button key={a} data-testid={`science-topic-${a}`} onClick={() => setFTopic(a)} className={`text-xs px-3 py-1 rounded-sm border transition-colors ${fTopic === a ? "bg-primary text-white border-primary" : "border-border text-muted-foreground hover:border-primary"}`}>{t(a)}</button>)}
      </div>

      {show && (
        <form onSubmit={publish} className="border border-border bg-white p-6 space-y-3" data-testid="study-form">
          <input required placeholder={t("Study title")} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={ic} />
          <textarea required placeholder={t("Abstract")} value={form.abstract} onChange={(e) => setForm({ ...form, abstract: e.target.value })} className={ic + " h-28"} />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input placeholder={t("Source URL / DOI")} value={form.source_url} onChange={(e) => setForm({ ...form, source_url: e.target.value })} className={ic} />
            <input placeholder={t("Topics (comma)")} value={form.topics} onChange={(e) => setForm({ ...form, topics: e.target.value })} className={ic} />
          </div>
          <button data-testid="study-publish-submit" className="bg-primary text-white px-4 py-2 rounded-sm text-sm font-semibold">{t("Publish")}</button>
        </form>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {studies.map((s) => (
          <article key={s.id} className="border border-border bg-white p-7 hover:shadow-md transition-all" data-testid="study-item">
            <FlaskConical className="text-secondary mb-3" size={20} />
            <div className="flex flex-wrap gap-1 mb-2">{(s.topics || []).map((tp) => <Badge key={tp}>{t(tp)}</Badge>)}</div>
            <h2 className="font-display text-xl font-bold tracking-tight mb-2">{s.title}</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">{s.abstract}</p>
            <p className="text-xs text-secondary font-semibold mt-3">{s.author_name}</p>
            {s.source_url && <a href={s.source_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-primary font-medium mt-2">{t("Read study")} <ExternalLink size={14} /></a>}
            <NuanceThread nuances={s.nuances} onAdd={(text, sources) => addNuance(s.id, text, sources)} testid={`study-nuance-${s.id}`} />
          </article>
        ))}
        {studies.length === 0 && <p className="text-sm text-muted-foreground">{t("No studies found for this topic.")}</p>}
      </div>
    </div>
  );
}
const ic = "w-full border border-border bg-background rounded-sm px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary";
